/**
 * PDF Export for Data Breach Cost Calculator
 * Generates comprehensive breach cost analysis reports
 */

import { BreachCostResult } from '@/types/calculator';
import type jsPDF from 'jspdf';

// Extend jsPDF type to include autoTable plugin
interface jsPDFWithPlugin extends jsPDF {
  lastAutoTable: {
    finalY: number;
  };
}

interface PDFExportParams {
  result: BreachCostResult;
  inputs: {
    industry: string;
    region: string;
    employeeCount: number;
    annualRevenue: number;
    recordCount: number;
    regulatedData: number;
    dataTypes: string[];
    maturityLevel: string;
    mttd: number;
    mttc: number;
    hasIR: boolean;
    hasInsurance: boolean;
  };
}

export async function generateBreachCostPDF(params: PDFExportParams) {
  const { default: jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const { result, inputs } = params;
  const doc = new jsPDF() as jsPDFWithPlugin;

  // Helper functions (defined at top to avoid hoisting issues)
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value);
  };

  const formatIndustry = (industry: string) => {
    return industry.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const formatRegion = (region: string) => {
    return region.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // ============================================================================
  // TITLE PAGE
  // ============================================================================

  // Company branding (text-based, clean design)
  doc.setFontSize(20);
  doc.setTextColor(37, 99, 235); // Blue-600 (brand color)
  doc.setFont('helvetica', 'bold');
  doc.text('INVENTIVE HQ', doc.internal.pageSize.width / 2, 50, { align: 'center' });

  // Main Title
  let titleYPos = 70;

  doc.setFontSize(28);
  doc.setTextColor(17, 24, 39); // Gray-900
  doc.setFont('helvetica', 'bold');
  doc.text('Data Breach Cost Analysis', doc.internal.pageSize.width / 2, titleYPos, { align: 'center' });

  titleYPos += 12;

  doc.setFontSize(18);
  doc.setTextColor(75, 85, 99); // Gray-600
  doc.setFont('helvetica', 'normal');
  doc.text('Comprehensive Risk Assessment Report', doc.internal.pageSize.width / 2, titleYPos, { align: 'center' });

  // Report Details Box
  titleYPos += 30;

  doc.setFillColor(249, 250, 251); // Gray-50
  doc.roundedRect(30, titleYPos, doc.internal.pageSize.width - 60, 60, 3, 3, 'F');

  titleYPos += 12;

  doc.setFontSize(12);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Organization:', 40, titleYPos);
  doc.setFont('helvetica', 'normal');
  doc.text(formatIndustry(inputs.industry) + ' Industry', 100, titleYPos);

  titleYPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Report Date:', 40, titleYPos);
  doc.setFont('helvetica', 'normal');
  doc.text(new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }), 100, titleYPos);

  titleYPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Records Analyzed:', 40, titleYPos);
  doc.setFont('helvetica', 'normal');
  doc.text(formatNumber(inputs.recordCount), 100, titleYPos);

  titleYPos += 10;

  doc.setFont('helvetica', 'bold');
  doc.text('Region:', 40, titleYPos);
  doc.setFont('helvetica', 'normal');
  doc.text(formatRegion(inputs.region), 100, titleYPos);

  // Executive Summary Highlight
  titleYPos += 25;

  doc.setDrawColor(37, 99, 235); // Blue-600 border
  doc.setLineWidth(2);
  doc.roundedRect(30, titleYPos, doc.internal.pageSize.width - 60, 35, 3, 3, 'S');

  titleYPos += 12;

  doc.setFontSize(11);
  doc.setTextColor(75, 85, 99);
  doc.setFont('helvetica', 'normal');
  doc.text('Estimated Total Breach Cost', doc.internal.pageSize.width / 2, titleYPos, { align: 'center' });

  titleYPos += 10;

  doc.setFontSize(24);
  doc.setTextColor(239, 68, 68); // Red for the cost (urgent/warning)
  doc.setFont('helvetica', 'bold');
  doc.text(formatCurrency(result.totalCost), doc.internal.pageSize.width / 2, titleYPos, { align: 'center' });

  // Footer on title page
  const footerY = doc.internal.pageSize.height - 30;

  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.text('This analysis is based on IBM\'s Cost of a Data Breach Report 2024', doc.internal.pageSize.width / 2, footerY, { align: 'center' });

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Confidential - For Internal Use Only', doc.internal.pageSize.width / 2, footerY + 6, { align: 'center' });

  doc.setFontSize(8);
  doc.setTextColor(156, 163, 175);
  doc.setFont('helvetica', 'normal');
  doc.text('inventivehq.com  |  (866) 903-2097  |  info@inventivehq.com', doc.internal.pageSize.width / 2, footerY + 12, { align: 'center' });

  // Add new page for the actual report content
  doc.addPage();

  let yPos = 20;

  // ============================================================================
  // HEADER
  // ============================================================================

  // Logo/Title
  doc.setFontSize(24);
  doc.setTextColor(37, 99, 235); // Blue-600 (brand color)
  doc.text('Data Breach Cost Analysis', 20, yPos);

  yPos += 10;
  doc.setFontSize(10);
  doc.setTextColor(100, 116, 139); // Gray-600
  doc.text('Generated by Inventive HQ | inventivehq.com', 20, yPos);

  yPos += 3;
  doc.setTextColor(156, 163, 175); // Gray-400
  doc.text(`Report Date: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}`, 20, yPos);

  yPos += 15;

  // ============================================================================
  // EXECUTIVE SUMMARY
  // ============================================================================

  doc.setFillColor(37, 99, 235); // Blue-600
  doc.rect(20, yPos - 5, 170, 35, 'F');

  doc.setFontSize(16);
  doc.setTextColor(255, 255, 255);
  doc.text('Executive Summary', 25, yPos);

  yPos += 10;
  doc.setFontSize(32);
  doc.setFont('helvetica', 'bold');
  doc.text(formatCurrency(result.totalCost), 25, yPos);

  yPos += 7;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text(`Estimated total breach cost for ${formatNumber(inputs.recordCount)} compromised records`, 25, yPos);

  yPos += 25;

  // ============================================================================
  // KEY METRICS
  // ============================================================================

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39); // Gray-900
  doc.setFont('helvetica', 'bold');
  doc.text('Key Metrics', 20, yPos);

  yPos += 8;

  autoTable(doc, {
    startY: yPos,
    head: [['Metric', 'Value']],
    body: [
      ['Total Breach Cost', formatCurrency(result.totalCost)],
      ['Cost Per Record', formatCurrency(result.costPerRecord)],
      ['Detection + Containment Time', `${result.breachLifecycleDays} days`],
      ['Detection Time (MTTD)', `${result.timeline.detectionDays} days`],
      ['Containment Time (MTTC)', `${result.timeline.containmentDays} days`],
      ['Cost Reduction from Controls', formatCurrency(result.costReductionFromControls)]
    ],
    theme: 'grid',
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 70, halign: 'right', fontStyle: 'bold' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // ============================================================================
  // ORGANIZATION PROFILE
  // ============================================================================

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Organization Profile', 20, yPos);

  yPos += 8;

  autoTable(doc, {
    startY: yPos,
    head: [['Attribute', 'Value']],
    body: [
      ['Industry', formatIndustry(inputs.industry)],
      ['Geographic Region', formatRegion(inputs.region)],
      ['Number of Employees', formatNumber(inputs.employeeCount)],
      ['Annual Revenue', formatCurrency(inputs.annualRevenue)],
      ['Records at Risk', formatNumber(inputs.recordCount)],
      ['Regulated Data Percentage', `${inputs.regulatedData}%`],
      ['Security Maturity Level', inputs.maturityLevel.charAt(0).toUpperCase() + inputs.maturityLevel.slice(1)],
      ['Incident Response Plan', inputs.hasIR ? 'Yes (Tested)' : 'No'],
      ['Cyber Insurance', inputs.hasInsurance ? 'Yes' : 'No']
    ],
    theme: 'striped',
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 100 },
      1: { cellWidth: 70, halign: 'right' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // Check if we need a new page
  if (yPos > 240) {
    doc.addPage();
    yPos = 20;
  }

  // ============================================================================
  // COST BREAKDOWN
  // ============================================================================

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Cost Breakdown by Category', 20, yPos);

  yPos += 8;

  const totalCost = result.totalCost;

  autoTable(doc, {
    startY: yPos,
    head: [['Category', 'Amount', '% of Total']],
    body: [
      [
        'Per-Record Costs',
        formatCurrency(result.breakdown.recordCost),
        `${((result.breakdown.recordCost / totalCost) * 100).toFixed(1)}%`
      ],
      [
        'Detection & Investigation',
        formatCurrency(result.breakdown.detection),
        `${((result.breakdown.detection / totalCost) * 100).toFixed(1)}%`
      ],
      [
        'Notification Costs',
        formatCurrency(result.breakdown.notification),
        `${((result.breakdown.notification / totalCost) * 100).toFixed(1)}%`
      ],
      [
        'Legal & Regulatory',
        formatCurrency(result.breakdown.legal),
        `${((result.breakdown.legal / totalCost) * 100).toFixed(1)}%`
      ],
      [
        'Regulatory Fines',
        formatCurrency(result.breakdown.fines),
        `${((result.breakdown.fines / totalCost) * 100).toFixed(1)}%`
      ],
      [
        'Business Disruption',
        formatCurrency(result.breakdown.businessDisruption),
        `${((result.breakdown.businessDisruption / totalCost) * 100).toFixed(1)}%`
      ],
      [
        'Post-Breach Response',
        formatCurrency(result.breakdown.postBreach),
        `${((result.breakdown.postBreach / totalCost) * 100).toFixed(1)}%`
      ]
    ],
    foot: [['TOTAL ESTIMATED COST', formatCurrency(totalCost), '100%']],
    theme: 'grid',
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
    footStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold', fontSize: 11 },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 90 },
      1: { cellWidth: 50, halign: 'right' },
      2: { cellWidth: 30, halign: 'right' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // ============================================================================
  // INSURANCE ANALYSIS (if applicable)
  // ============================================================================

  if (inputs.hasInsurance && result.insurance.coverage > 0) {
    if (yPos > 220) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.setTextColor(17, 24, 39);
    doc.setFont('helvetica', 'bold');
    doc.text('Cyber Insurance Analysis', 20, yPos);

    yPos += 8;

    autoTable(doc, {
      startY: yPos,
      head: [['Item', 'Amount']],
      body: [
        ['Total Breach Cost', formatCurrency(result.totalCost)],
        ['Insurance Coverage (65%)', `- ${formatCurrency(result.insurance.coverage)}`],
        ['Your Out-of-Pocket Cost', formatCurrency(result.insurance.outOfPocket)],
        ['Estimated Premium Increase (3 years)', formatCurrency(result.insurance.premiumIncrease)]
      ],
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 10 },
      columnStyles: {
        0: { cellWidth: 100 },
        1: { cellWidth: 70, halign: 'right', fontStyle: 'bold' }
      }
    });

    yPos = doc.lastAutoTable.finalY + 15;
  }

  // ============================================================================
  // RECOMMENDATIONS
  // ============================================================================

  if (result.recommendations && result.recommendations.length > 0) {
    if (yPos > 200) {
      doc.addPage();
      yPos = 20;
    }

    doc.setFontSize(14);
    doc.setTextColor(17, 24, 39);
    doc.setFont('helvetica', 'bold');
    doc.text('Top Recommendations to Reduce Breach Costs', 20, yPos);

    yPos += 8;

    const recommendationData = result.recommendations.map((rec, index) => [
      `${index + 1}`,
      rec.action,
      formatCurrency(rec.costSavings),
      formatCurrency(rec.investmentNeeded),
      `${rec.roi.toFixed(0)}%`
    ]);

    autoTable(doc, {
      startY: yPos,
      head: [['#', 'Action', 'Savings', 'Investment', 'ROI']],
      body: recommendationData,
      theme: 'grid',
      headStyles: { fillColor: [34, 197, 94], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 10, halign: 'center' },
        1: { cellWidth: 75 },
        2: { cellWidth: 30, halign: 'right' },
        3: { cellWidth: 30, halign: 'right' },
        4: { cellWidth: 25, halign: 'right', fontStyle: 'bold' }
      }
    });

    yPos = doc.lastAutoTable.finalY + 15;
  }

  // ============================================================================
  // INDUSTRY COMPARISON
  // ============================================================================

  if (yPos > 220) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(14);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Industry Comparison', 20, yPos);

  yPos += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(75, 85, 99); // Gray-600

  const splitText = doc.splitTextToSize(result.industryComparison, 170);
  doc.text(splitText, 20, yPos);

  yPos += (splitText.length * 5) + 10;

  autoTable(doc, {
    startY: yPos,
    head: [['Metric', 'Your Organization', 'Industry Average']],
    body: [
      [
        'Estimated Breach Cost',
        formatCurrency(result.totalCost),
        formatCurrency(result.industryComparisonData.industryAverage)
      ],
      [
        'Breach Lifecycle',
        `${result.breachLifecycleDays} days`,
        '258 days (global avg)'
      ]
    ],
    theme: 'striped',
    headStyles: { fillColor: [37, 99, 235], textColor: 255, fontStyle: 'bold' },
    styles: { fontSize: 10 },
    columnStyles: {
      0: { cellWidth: 70 },
      1: { cellWidth: 50, halign: 'right', fontStyle: 'bold' },
      2: { cellWidth: 50, halign: 'right' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // ============================================================================
  // FOOTER / METHODOLOGY
  // ============================================================================

  if (yPos > 220) {
    doc.addPage();
    yPos = 20;
  }

  doc.setFontSize(12);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'bold');
  doc.text('Methodology & Sources', 20, yPos);

  yPos += 8;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(75, 85, 99);

  const methodology = [
    'This analysis is based on IBM\'s Cost of a Data Breach Report 2024, conducted by Ponemon Institute.',
    '',
    'The report analyzed 604 real-world data breaches across 17 countries and regions between March 2023',
    'and February 2024. Costs include both direct and indirect expenses such as detection, investigation,',
    'notification, legal fees, regulatory fines, business disruption, customer churn, and post-breach response.',
    '',
    'Industry-specific multipliers, regional adjustments, and scale factors are applied based on your',
    'organization\'s profile. Security maturity level, incident response capabilities, and breach lifecycle',
    'times significantly impact total costs.',
    '',
    'DISCLAIMER: These estimates are for planning purposes only and do not guarantee actual breach costs.',
    'Actual costs vary based on attack vector, data sensitivity, response speed, and many other factors.',
    'Consult with cybersecurity and legal professionals for specific guidance.'
  ];

  methodology.forEach(line => {
    doc.text(line, 20, yPos);
    yPos += 4;
  });

  yPos += 10;

  // ============================================================================
  // ABOUT INVENTIVE HQ
  // ============================================================================

  // Always start on a new page
  doc.addPage();
  yPos = 20;

  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;

  // Section header
  doc.setFillColor(37, 99, 235);
  doc.rect(20, yPos - 5, 170, 12, 'F');

  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('About Inventive HQ', 25, yPos + 3);

  yPos += 20;

  // Company description
  doc.setFontSize(10);
  doc.setTextColor(17, 24, 39);
  doc.setFont('helvetica', 'normal');

  const aboutText = [
    'Inventive HQ is a leading cybersecurity and IT services provider specializing in protecting businesses',
    'from cyber threats. We combine advanced technology, proven methodologies, and expert guidance to',
    'help organizations of all sizes build resilient security programs and reduce breach risk.',
    '',
    'Our mission is to make enterprise-grade cybersecurity accessible and affordable for growing businesses.'
  ];

  aboutText.forEach(line => {
    doc.text(line, 20, yPos);
    yPos += 4.5;
  });

  yPos += 8;

  // Services section - 4 column mega menu layout
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(17, 24, 39);
  doc.text('Our Services', 20, yPos);

  yPos += 8;

  // Define service columns matching the mega menu
  const serviceColumns = [
    {
      title: 'Security Services',
      services: [
        { name: 'Virtual CISO (vCISO)', url: 'https://inventivehq.com/services/vciso' },
        { name: 'Risk Assessment', url: 'https://inventivehq.com/services/compliance' },
        { name: 'Penetration Testing', url: 'https://inventivehq.com/services/penetration-testing' },
        { name: 'Security Awareness Training', url: 'https://inventivehq.com/services/security-awareness' },
        { name: 'Incident Response', url: 'https://inventivehq.com/services/incident-response' },
        { name: 'Vendor Risk Management', url: 'https://inventivehq.com/services/vendor-risk-management' },
        { name: 'Security Policies', url: 'https://inventivehq.com/services/compliance' },
        { name: 'Cloud Security Assessment', url: 'https://inventivehq.com/services/cloud-infrastructure-management' },
        { name: 'Email Security', url: 'https://inventivehq.com/services/small-business-cybersecurity' }
      ]
    },
    {
      title: 'Compliance Services',
      services: [
        { name: 'Compliance Overview', url: 'https://inventivehq.com/services/compliance' },
        { name: 'HIPAA Compliance', url: 'https://inventivehq.com/services/compliance/hipaa' },
        { name: 'SOC 2 Compliance', url: 'https://inventivehq.com/services/compliance/soc2' },
        { name: 'NIST Compliance', url: 'https://inventivehq.com/services/compliance/nist' },
        { name: 'PCI-DSS Compliance', url: 'https://inventivehq.com/services/compliance/pci-dss' },
        { name: 'GDPR Compliance', url: 'https://inventivehq.com/services/compliance/gdpr' }
      ]
    },
    {
      title: 'Managed Services',
      services: [
        { name: '24/7 Detection & Response', url: 'https://inventivehq.com/services/24-7-detection-and-response' },
        { name: 'Backup & Disaster Recovery', url: 'https://inventivehq.com/services/backup-and-ransomware-defense' },
        { name: 'Security Operations Center (SOC)', url: 'https://inventivehq.com/services/24-7-detection-and-response' },
        { name: 'Vulnerability Management', url: 'https://inventivehq.com/services/vulnerability-management' }
      ]
    },
    {
      title: 'Cloud Solutions',
      services: [
        { name: 'Cloud Migration', url: 'https://inventivehq.com/services/cloud-infrastructure-management' },
        { name: 'Cloud Optimization', url: 'https://inventivehq.com/services/cloud-infrastructure-management' },
        { name: 'Multi-Cloud Strategy', url: 'https://inventivehq.com/services/cloud-infrastructure-management' }
      ]
    }
  ];

  // Column layout: 4 columns, 45 width each (with gaps)
  const columnWidth = 42;
  const columnGap = 4;
  const startX = 20;

  serviceColumns.forEach((column, colIndex) => {
    const colX = startX + (colIndex * (columnWidth + columnGap));
    let colY = yPos;

    // Column header
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(75, 79, 88); // Gray-700 (#4B4F58)
    doc.text(column.title, colX, colY);

    colY += 5;

    // Services in column
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(37, 99, 235); // Blue-600 for links

    column.services.forEach((service) => {
      // Make service name clickable
      doc.textWithLink(service.name, colX, colY, { url: service.url });
      colY += 3.5;
    });
  });

  // Find max column height and advance yPos
  const maxServicesCount = Math.max(...serviceColumns.map(col => col.services.length));
  yPos += 5 + (maxServicesCount * 3.5) + 8;

  // Why Choose Us section
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(17, 24, 39);
  doc.text('Why Choose Inventive HQ?', 20, yPos);

  yPos += 8;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  const whyChoose = [
    'Protecting organizations across healthcare, finance, legal, and technology sectors',
    'Vendor-Agnostic Expertise - We recommend the best solutions for your needs, not what earns us commissions',
    'Rapid Response - 24/7 security operations center with under 15 minute response times',
    'Transparent Pricing - No hidden fees, clear ROI, and flexible engagement models'
  ];

  whyChoose.forEach(item => {
    // Draw bullet point
    doc.setFillColor(37, 99, 235); // Blue-600
    doc.circle(22, yPos - 1, 1, 'F');

    const wrapped = doc.splitTextToSize(item, 165);
    doc.text(wrapped, 28, yPos);
    yPos += (wrapped.length * 4) + 3;
  });

  // Space before CTA at bottom
  const ctaHeight = 52;
  const ctaOffset = 16 + (pageHeight * 0.05); // Move up 5% from bottom
  const availableSpace = pageHeight - ctaOffset - yPos;

  // If CTA won't fit, start new page
  if (availableSpace < ctaHeight) {
    doc.addPage();
    yPos = 20;
  } else {
    // Position CTA near bottom of page (moved up 5%)
    yPos = pageHeight - ctaHeight - ctaOffset;
  }

  // Contact CTA - moved to bottom
  doc.setFillColor(59, 130, 246); // Blue-500
  doc.roundedRect(20, yPos, 170, ctaHeight, 3, 3, 'F');

  yPos += 10;

  doc.setFontSize(14);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.text('Ready to Reduce Your Breach Risk?', pageWidth / 2, yPos, { align: 'center' });

  yPos += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Schedule a free consultation to discuss your cybersecurity needs and get a customized', pageWidth / 2, yPos, { align: 'center' });

  yPos += 5;

  doc.text('security roadmap tailored to your organization.', pageWidth / 2, yPos, { align: 'center' });

  yPos += 10;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Contact: inventivehq.com/contact', pageWidth / 2, yPos, { align: 'center' });

  yPos += 5;
  doc.text('Phone: (866) 903-2097', pageWidth / 2, yPos, { align: 'center' });

  yPos += 7;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('2305 Historic Decatur Rd, Suite 100', pageWidth / 2, yPos, { align: 'center' });

  yPos += 4;
  doc.text('San Diego, CA 92106', pageWidth / 2, yPos, { align: 'center' });

  yPos += 15;

  // Footer on every page
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(156, 163, 175); // Gray-400
    doc.text(
      `Page ${i} of ${pageCount}`,
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 10,
      { align: 'center' }
    );
    doc.text(
      'Generated by Inventive HQ Data Breach Cost Calculator | inventivehq.com',
      doc.internal.pageSize.width / 2,
      doc.internal.pageSize.height - 6,
      { align: 'center' }
    );
  }

  // ============================================================================
  // SAVE THE PDF
  // ============================================================================

  const fileName = `Breach-Cost-Analysis-${formatIndustry(inputs.industry).replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.pdf`;
  doc.save(fileName);
}
